import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  tenantsApiService,
  Tenant,
  CreateTenantDto,
  UpdateTenantDto,
  TenantFilters,
} from '../api/tenants-api';

// Estado inicial
interface TenantsState {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: TenantFilters;
  stats: {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    pending: number;
  } | null;
}

const initialState: TenantsState = {
  tenants: [],
  currentTenant: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    status: '',
    plan: '',
    search: '',
  },
  stats: null,
};

// Async thunks
export const fetchTenants = createAsyncThunk(
  'tenants/fetchTenants',
  async (filters: TenantFilters = {}, { rejectWithValue }) => {
    try {
      const response = await tenantsApiService.getTenants(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar tenants'
      );
    }
  }
);

export const fetchTenant = createAsyncThunk(
  'tenants/fetchTenant',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await tenantsApiService.getTenant(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar tenant'
      );
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenants/createTenant',
  async (data: CreateTenantDto, { rejectWithValue }) => {
    try {
      const response = await tenantsApiService.createTenant(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear tenant'
      );
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenants/updateTenant',
  async (
    { id, data }: { id: string; data: UpdateTenantDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await tenantsApiService.updateTenant(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar tenant'
      );
    }
  }
);

export const deleteTenant = createAsyncThunk(
  'tenants/deleteTenant',
  async (id: string, { rejectWithValue }) => {
    try {
      await tenantsApiService.deleteTenant(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar tenant'
      );
    }
  }
);

export const updateTenantStatus = createAsyncThunk(
  'tenants/updateTenantStatus',
  async (
    { id, status }: { id: string; status: Tenant['status'] },
    { rejectWithValue }
  ) => {
    try {
      const response = await tenantsApiService.updateTenantStatus(id, status);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del tenant'
      );
    }
  }
);

export const fetchTenantStats = createAsyncThunk(
  'tenants/fetchTenantStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tenantsApiService.getTenantStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Slice
const tenantsSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<TenantFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {
        status: '',
        plan: '',
        search: '',
      };
    },
    setCurrentTenant: (state, action: PayloadAction<Tenant | null>) => {
      state.currentTenant = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    // Acciones síncronas para la saga
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setTenants: (state, action: PayloadAction<Tenant[]>) => {
      state.tenants = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<TenantsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStats: (state, action: PayloadAction<TenantsState['stats']>) => {
      state.stats = action.payload;
    },
    addTenant: (state, action: PayloadAction<Tenant>) => {
      state.tenants.unshift(action.payload);
    },
    updateTenantInList: (state, action: PayloadAction<Tenant>) => {
      const index = state.tenants.findIndex(
        tenant => tenant.id === action.payload.id
      );
      if (index !== -1) {
        state.tenants[index] = action.payload;
      }
    },
    removeTenant: (state, action: PayloadAction<string>) => {
      state.tenants = state.tenants.filter(
        tenant => tenant.id !== action.payload
      );
    },
  },
  extraReducers: builder => {
    // Fetch tenants
    builder
      .addCase(fetchTenants.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTenants.fulfilled,
        (state, action) => {
          const payload = action.payload as any;
          state.loading = false;
          state.tenants = payload.tenants || payload.data || [];
          state.pagination = {
            page: payload.page || 1,
            limit: payload.limit || 10,
            total: payload.total || 0,
            totalPages: payload.totalPages || 1,
          };
        }
      )
      .addCase(fetchTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch tenant
    builder
      .addCase(fetchTenant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTenant.fulfilled,
        (state, action: PayloadAction<Tenant>) => {
          state.loading = false;
          state.currentTenant = action.payload;
        }
      )
      .addCase(fetchTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create tenant
    builder
      .addCase(createTenant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createTenant.fulfilled,
        (state, action: PayloadAction<Tenant>) => {
          state.loading = false;
          state.tenants.unshift(action.payload);
          state.pagination.total += 1;
        }
      )
      .addCase(createTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update tenant
    builder
      .addCase(updateTenant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateTenant.fulfilled,
        (state, action: PayloadAction<Tenant>) => {
          state.loading = false;
          const index = state.tenants.findIndex(
            tenant => tenant.id === action.payload.id
          );
          if (index !== -1) {
            state.tenants[index] = action.payload;
          }
          if (state.currentTenant?.id === action.payload.id) {
            state.currentTenant = action.payload;
          }
        }
      )
      .addCase(updateTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete tenant
    builder
      .addCase(deleteTenant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteTenant.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.tenants = state.tenants.filter(
            tenant => tenant.id !== action.payload
          );
          state.pagination.total -= 1;
          if (state.currentTenant?.id === action.payload) {
            state.currentTenant = null;
          }
        }
      )
      .addCase(deleteTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update tenant status
    builder
      .addCase(updateTenantStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateTenantStatus.fulfilled,
        (state, action: PayloadAction<Tenant>) => {
          state.loading = false;
          const index = state.tenants.findIndex(
            tenant => tenant.id === action.payload.id
          );
          if (index !== -1) {
            state.tenants[index] = action.payload;
          }
          if (state.currentTenant?.id === action.payload.id) {
            state.currentTenant = action.payload;
          }
        }
      )
      .addCase(updateTenantStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch tenant stats
    builder
      .addCase(fetchTenantStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchTenantStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentTenant,
  clearError,
  setLoading,
  setError,
  setTenants,
  setPagination,
  setStats,
  addTenant,
  updateTenantInList,
  removeTenant,
} = tenantsSlice.actions;

export default tenantsSlice.reducer;
